import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Building2, Wrench, Trash2, Zap, Paintbrush, Home as HomeIcon, Clock, Award, Users } from 'lucide-react';

function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [countersStarted, setCountersStarted] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const heroSlides = [
    {
      tagline: "VISION. INTEGRITY. RESULTS. EXCELLENCE.",
      headline: "Mission-Critical",
      headlineAccent: "Solutions",
      description: "Rapid deployment. Unwavering reliability. Serving government, defense, and commercial clients where precision and accountability define success.",
      image: "https://images.unsplash.com/photo-1594581979864-36977b15d0dc?fm=webp&q=80&w=2400"
    },
    {
      tagline: "DIVERSIFIED. DEPENDABLE. DILIGENT.",
      headline: "Nationwide & Worldwide",
      headlineAccent: "Services",
      description: "From retail construction to critical infrastructure—delivering excellence across every sector and every location.",
      image: "https://images.unsplash.com/photo-1500252185289-40ca85eb23a7?fm=webp&q=80&w=2400"
    },
    {
      tagline: "PRECISION. RELIABILITY. INNOVATION.",
      headline: "Emergency Response &",
      headlineAccent: "Rapid Deployment",
      description: "48-hour nationwide deployment for critical facilities. Temporary power, modular shelters, and life-support systems for FEMA, DoD, and emergency operations.",
      image: "https://images.unsplash.com/photo-1508530786855-dfea35260b8d?fm=webp&q=80&w=2400"
    },
    {
      tagline: "STRENGTH. QUALITY. COMMITMENT.",
      headline: "Government",
      headlineAccent: "Contracting Ready",
      description: "Pursuing federal opportunities with 17 NAICS codes, comprehensive compliance knowledge, and unwavering commitment to mission success.",
      image: "https://images.unsplash.com/photo-1485394595691-5411947d63a4?fm=webp&q=80&w=2400"
    }
  ];

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          
          // Trigger counter animation when stats section is in view
          const statsSection = document.getElementById('stats');
          if (statsSection && !countersStarted) {
            const rect = statsSection.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom >= 0) {
              setCountersStarted(true);
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [countersStarted]);

  // Rotating hero slides effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlideIndex((prevIndex) => 
        (prevIndex + 1) % heroSlides.length
      );
    }, 8000); // Change every 8 seconds (was 5)

    return () => clearInterval(interval);
  }, [heroSlides.length]);

  const stats = [
    { number: 80, suffix: '+', label: 'Years of Combined Experience', icon: <Clock /> },
    { number: 35, suffix: '%', label: 'Year-over-Year Growth', icon: <Award /> },
    { number: 100, suffix: '+', label: 'Projects Completed Nationwide', icon: <Building2 /> },
    { number: 40000, suffix: '+', label: 'On-Site Hours', icon: <Users /> }
  ];

  const services = [
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Government Contracting',
      description: 'Specialized in federal, state, and municipal projects as both prime contractor and subcontractor. We deliver compliant, on-schedule solutions for government facilities worldwide.',
      image: 'https://images.unsplash.com/photo-1761002068501-280b66da7ee5?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/government-contracting'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Civil & Commercial Construction',
      description: 'From large-scale civil works to premier commercial developments, we deliver structures built with precision, strength, and timeless quality.',
      image: 'https://images.unsplash.com/photo-1599995903128-531fc7fb694b?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/private-sector/civil-commercial-construction'
    },
    {
      icon: <Wrench className="w-12 h-12" />,
      title: 'Project Management',
      description: 'We provide seamless, end-to-end project management — ensuring every build stays on schedule, on budget, and executed to the highest standards.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/private-sector/project-management'
    },
    {
      icon: <Trash2 className="w-12 h-12" />,
      title: 'Demolition & Site Preparation',
      description: 'Safe, efficient, and thorough. We handle demolition, debris removal, land clearing, and site preparation to create the foundation for success.',
      image: 'https://images.unsplash.com/photo-1574757987642-5755f0839101?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/private-sector/demolition-site-preparation'
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Plumbing, Electrical & Essential Systems',
      description: 'We handle plumbing, electrical, and other vital building systems with meticulous precision. Every installation is completed to the highest safety standards.',
      image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/private-sector/plumbing-electrical-systems'
    },
    {
      icon: <Paintbrush className="w-12 h-12" />,
      title: 'Interior Finishes & Custom Detailing',
      description: 'From drywall and sheetrock to painting, flooring, and custom detailing, our finishing services create refined spaces that are as functional as they are sophisticated.',
      image: 'https://images.unsplash.com/photo-1482731215275-a1f151646268?fm=webp&q=70&auto=format&fit=max&w=800',
      link: '/private-sector/interior-finishes'
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with Fade Transition - Only load active and adjacent slides */}
        {heroSlides.map((slide, index) => {
          // Only render background image for current slide and adjacent slides for smooth transitions
          const isActive = currentSlideIndex === index;
          const isAdjacent = Math.abs(currentSlideIndex - index) <= 1 || 
            (currentSlideIndex === 0 && index === heroSlides.length - 1) ||
            (currentSlideIndex === heroSlides.length - 1 && index === 0);
          
          return (
            <div 
              key={index}
              className="absolute inset-0 transition-opacity duration-1000"
              style={{ 
                backgroundImage: (isActive || isAdjacent) ? `url(${slide.image})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: isActive ? 1 : 0,
                zIndex: isActive ? 1 : 0
              }}
            >
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
          );
        })}

        {/* Animated brass accent overlays */}
        <div className="absolute inset-0 brass-lighting" style={{ zIndex: 2 }}></div>
        <div className="absolute inset-0 geometric-brass" style={{ zIndex: 2 }}></div>
        <div className="absolute inset-0 shimmer-wave" style={{ zIndex: 2 }}></div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="inline-block border-t-2 border-b-2 border-gold py-4 px-8">
              <span 
                className="text-gold text-sm font-semibold tracking-[0.3em] uppercase transition-opacity duration-500" 
                data-testid="hero-tagline"
                key={currentSlideIndex}
                style={{
                  animation: 'fadeInOut 5s ease-in-out infinite'
                }}
              >
                {heroSlides[currentSlideIndex].tagline}
              </span>
            </div>
          </div>
          <h1 
            className="text-6xl md:text-8xl font-bold text-white mb-8 leading-tight font-serif transition-all duration-500" 
            data-testid="hero-title" 
            style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            key={`headline-${currentSlideIndex}`}
          >
            {heroSlides[currentSlideIndex].headline}<br />
            <span className="text-gold">{heroSlides[currentSlideIndex].headlineAccent}</span>
          </h1>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <p 
            className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-500" 
            style={{ color: 'var(--cream)' }} 
            data-testid="hero-subtitle"
            key={`description-${currentSlideIndex}`}
          >
            {heroSlides[currentSlideIndex].description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link 
              to="/contact"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 hover:shadow-2xl uppercase tracking-wider"
              data-testid="hero-cta-button"
            >
              Start Your Project
            </Link>
            <Link 
              to="/contact"
              className="inline-block bg-transparent border-2 border-gold hover:bg-gold hover:text-black text-gold font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              24/7 Emergency Response
            </Link>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-3 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlideIndex(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlideIndex === index 
                  ? 'bg-gold w-8' 
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Services Section - Black Background with Gold Light Effects */}
      <section className="bg-black py-32 relative overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Our Services</span>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Providing Quality <span className="text-gold">Services</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto mt-6">
              Specialized solutions for both Public and Private sector clients
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index}
                to={service.link}
                className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105 hover:shadow-2xl"
                data-testid={`service-card-${index}`}
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${service.image})` }}
                ></div>
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-black/70 group-hover:from-black/95 group-hover:via-black/80 transition-all"></div>
                
                {/* Content */}
                <div className="relative z-10 p-10">
                  <div className="text-gold mb-6 group-hover:scale-110 transition-transform">
                    {service.icon}
                  </div>
                  <h4 className="text-2xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>{service.title}</h4>
                  <p className="text-gray-300 leading-relaxed">{service.description}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/services"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Who We Serve</span>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Industries <span className="text-gold">We Serve</span>
            </h2>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto mt-6">
              Specialized expertise across critical sectors nationwide
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            <Link 
              to="/industries/government-defense"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1708733818543-8a895c354151?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Government & Defense</h4>
              </div>
            </Link>

            <Link 
              to="/industries/healthcare"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Healthcare</h4>
              </div>
            </Link>

            <Link 
              to="/industries/commercial-real-estate"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Commercial Real Estate</h4>
              </div>
            </Link>

            <Link 
              to="/industries/retail-hospitality"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1441986300917-64674bd600d8?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Retail, Restaurant & Hospitality</h4>
              </div>
            </Link>

            <Link 
              to="/industries/infrastructure"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1581094271901-8022df4466f9?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Infrastructure</h4>
              </div>
            </Link>

            <Link 
              to="/industries/industrial-manufacturing"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1565793298595-6a879b1d9492?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Industrial & Manufacturing</h4>
              </div>
            </Link>

            <Link 
              to="/industries/multi-family"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Multi-Family Residential</h4>
              </div>
            </Link>

            <Link 
              to="/industries/education"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1562774053-701939374585?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Education</h4>
              </div>
            </Link>

            <Link 
              to="/industries/energy"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Energy</h4>
              </div>
            </Link>

            <Link 
              to="/industries/financial-institutions"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Financial Institutions</h4>
              </div>
            </Link>

            <Link 
              to="/industries/convenience-stores"
              className="relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group hover:transform hover:scale-105"
            >
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1604719312566-8912e9227c6a?fm=webp&q=70&auto=format&fit=max&w=1920)' }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/60"></div>
              <div className="relative z-10 p-6 h-48 flex flex-col justify-end">
                <h4 className="text-white font-bold text-lg">Convenience Stores</h4>
              </div>
            </Link>
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/industries"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              View All Industries
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Preview Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Client Reviews</span>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              What Our <span className="text-gold">Clients Say</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-black border border-gold/20 rounded-xl p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-gold fill-gold" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">"Williams Diversified handled our 47-store rollout across 12 states. Consistent quality, on-time delivery, and one point of contact made managing this massive project simple."</p>
              <span className="text-gold text-sm font-medium px-3 py-1 bg-gold/10 rounded-full">Retail</span>
            </div>
            <div className="bg-black border border-gold/20 rounded-xl p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-gold fill-gold" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">"24/7 emergency response has saved us multiple times. HVAC failure at midnight? Technicians on site within 2 hours. Invaluable partner."</p>
              <span className="text-gold text-sm font-medium px-3 py-1 bg-gold/10 rounded-full">Facility Maintenance</span>
            </div>
            <div className="bg-black border border-gold/20 rounded-xl p-8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-5 h-5 text-gold fill-gold" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-gray-300 leading-relaxed mb-6">"Our surgical suite renovation required zero contamination and minimal downtime. Williams Diversified exceeded every expectation."</p>
              <span className="text-gold text-sm font-medium px-3 py-1 bg-gold/10 rounded-full">Healthcare</span>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/testimonials"
              className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-4 px-10 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Read All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Ready to Start Your <span className="text-gold">Project?</span>
          </h2>
          <p className="text-gray-300 text-xl mb-10 leading-relaxed">
            Let&apos;s discuss how we can bring your vision to life with our expertise and dedication to excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link 
              to="/contact"
              className="bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              Get In Touch
            </Link>
            <Link 
              to="/services"
              className="border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
            >
              View Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
